import { useTranslation } from "react-i18next";
import {
  getVocabularyList,
  useCreateVocabularyList,
  useGetVocabularyList,
} from "@packages/api/madlenAPI";
import {
  VocabularyListResponseDto,
  VocabularyListCreateRequestDto,
} from "@packages/api/madlenAPI.schemas";
import CreateModal, {
  CreateModalProps,
} from "../../../components/create-modal";
import SingleVocabulary from "../../../components/vocabularies/SingleVocabulary";
import GenericToolCreationPage from "../../../components/tool_components/GenericToolCreationPage";
import CardImage from "@features/tools/components/tool_components/CardImage";
import i18n from "@packages/i18n/config";

interface VocabularyWord {
  id: number;
  word: string;
  usage: string;
  explanation: string;
  imageUrl?: string;
}

const CreateVocabularyView = () => {
  const { t } = useTranslation();

  const CreateModalWrapper = ({
    initialInput,
    onSubmit,
    isLoading,
    error,
  }: Partial<CreateModalProps>) => (
    <CreateModal
      title={t("vocabularies.result.loading.title")}
      backPath="/content/vocabularies"
      themeColor="cyan"
      isEnglishPlatform={true}
      loadingTitle={t("vocabularies.result.loading.title")}
      errorTitle={t("vocabularies.result.error.title")}
      errorDescription={t("vocabularies.result.error.description")}
      submitButtonText={t("common.generate")}
      initialInput={initialInput!}
      onSubmit={async (input) => {
        await onSubmit!(input);
      }}
      isLoading={isLoading!}
      error={error!}
      enableFileUpload={true}
    />
  );

  const ToolCardWrapper = ({
    toolData,
    onCreateAgain,
  }: {
    toolData: VocabularyListResponseDto;
    onCreateAgain: () => void;
  }) => (
    <div className="flex flex-col overflow-y-auto px-4">
      <div className="mb-4">
        <CardImage
          imageUrl={toolData.imageUrl}
          courseName={toolData.courseName}
          gradeLevel={toolData.gradeLevel}
          theme="amber"
        />
      </div>
      <SingleVocabulary
        id={toolData.id}
        isEditable={false}
        vocabulary={toolData.vocabulary as VocabularyWord[]}
        courseName={toolData.courseName}
        gradeLevel={toolData.gradeLevel}
        topic={toolData.title || toolData.prompt || ""}
        imageUrl={toolData.imageUrl}
        author={toolData.authorName}
        createdAt={toolData.createdAt}
        onCreateAgain={onCreateAgain}
        contentLanguage={toolData.contentLanguage || i18n.language}
      />
    </div>
  );

  return (
    <GenericToolCreationPage
      toolType="vocabularies"
      useCreateTool={useCreateVocabularyList}
      useGetTool={useGetVocabularyList}
      getTool={getVocabularyList}
      ToolCard={ToolCardWrapper}
      CreateModalWrapper={CreateModalWrapper}
      toolDataExtractor={(data: VocabularyListResponseDto) =>
        data?.vocabulary && data?.vocabulary?.length > 0
      }
    />
  );
};

export default CreateVocabularyView;
